import React, { FC, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useDispatch } from 'react-redux';
import ChatMessage from './ChatMessageShadcn';
import {
  addLocalChatMessage,
  startChatStream,
  dismissTicketConfirm,
  confirmCreateTicket,
  toggleTicketList,
  clearAiHelperContext,
  clearVerificationErrorContext,
  loadMoreChatHistoryAction,
  cancelTicketConfirm,
} from '../../redux/actions/chatbot';
import imgURL from '../../lib/imgURL';
import TicketForm from './TicketForm';
import TicketList from './TicketList';
import TicketCreationSuccess from './TicketCreationSuccess';
import TicketDetail from './TicketDetail';
import { TicketDetailItem } from './TicketDetail';
import { BACKEND_STEP_TO_DISPLAY_LABEL } from '../SideBar/sidebar.constant';
import './style.scss';

const ChatPanel: FC<any> = (props): JSX.Element | null => {
  const {
    isOpen,
    onClose,
    messages = [],
    isLoading = false,
    suggestions = [],
    ticketConfirm = null,
    ticketCreating = false,
    ticketError = null,
    ticketCount = 0,
    addLocalMessage,
    currentOnboardingStep = '',
    aiHelperFieldLabel = null,
    showTicketList = false,
    tickets = [],
    verificationErrorContext = null,
    hasMoreHistory = false,
    isLoadingMoreHistory = false,
    totalHistoryCount = 0,
    loadMoreHistory,
  } = props;

  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);
  const rafPendingRef = useRef<number | null>(null);
  const streamControllerRef = useRef<AbortController | null>(null);
  const isPrependingRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const pendingErrorStreamRef = useRef<(() => void) | null>(null);

  const followStream = () => {
    if (!isPinnedRef.current) return;
    if (rafPendingRef.current != null) return;
    rafPendingRef.current = requestAnimationFrame(() => {
      rafPendingRef.current = null;
      const node = scrollBodyRef.current;
      node?.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
    });
  };

  const smoothScrollToBottom = () => {
    const node = scrollBodyRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
    isPinnedRef.current = true;
    setShowScrollBtn(false);
    const pending = pendingErrorStreamRef.current;
    pendingErrorStreamRef.current = null;
    pending?.();
  };

  const handleScroll = () => {
    const node = scrollBodyRef.current;
    if (!node) return;
    const atBottom =
      node.scrollHeight - node.scrollTop - node.clientHeight < 60;
    isPinnedRef.current = atBottom;
    setShowScrollBtn(
      !!pendingErrorStreamRef.current ||
        (!atBottom && node.scrollHeight > node.clientHeight),
    );


    const oldestId = messages[0]?.id;
    if (node.scrollTop < 80 && hasMoreHistory && !isLoadingMoreHistory && oldestId) {
      isPrependingRef.current = true;
      prevScrollHeightRef.current = node.scrollHeight;
      loadMoreHistory(oldestId);
    }
  };

  useEffect(() => {
    const node = scrollBodyRef.current;
    if (isPrependingRef.current && node && !isPinnedRef.current) {
      node.scrollTop = node.scrollHeight - prevScrollHeightRef.current;
      isPrependingRef.current = false;
      return;
    }
    isPrependingRef.current = false;
    followStream();
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isOpen) return;
    isPinnedRef.current = true;
    requestAnimationFrame(() => {
      const node = scrollBodyRef.current;
      if (node) node.scrollTop = node.scrollHeight;
    });
  }, [isOpen]);

  useEffect(() => {
    return () => {
      streamControllerRef.current?.abort();
      streamControllerRef.current = null;
      if (rafPendingRef.current != null) {
        cancelAnimationFrame(rafPendingRef.current);
        rafPendingRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !verificationErrorContext) return;
    const { errors, route, isAlreadyOpened } = verificationErrorContext;
    if (!errors || errors.length === 0) return;

    streamControllerRef.current?.abort();
    pendingErrorStreamRef.current = null;

    const errorSummary = errors.slice(0, 5).join(' | ');
    const systemMessage = `[SYSTEM] The user encountered the following error(s) on ${route}: ${errorSummary}. Provide helpful guidance to resolve this.`;

    const startErrorStream = () => {
      isPinnedRef.current = true;
      streamControllerRef.current = startChatStream(
        dispatch,
        systemMessage,
        currentOnboardingStep,
        null,
        verificationErrorContext,
        handleOpenTicketForm,
      );
    };

    if (isAlreadyOpened) {
      // Wait for the user to click the scroll FAB before streaming
      isPinnedRef.current = false;
      pendingErrorStreamRef.current = startErrorStream;
      setShowScrollBtn(true);
    } else {
      setShowScrollBtn(false);
      startErrorStream();
    }

    dispatch(clearVerificationErrorContext());
  }, [isOpen, verificationErrorContext]);

  const handleSend = (text?: string) => {
    dispatch(dismissTicketConfirm());
    const msg = (text || inputValue).trim();
    if (!msg || isLoading) return;
    streamControllerRef.current?.abort();
    pendingErrorStreamRef.current = null;
    isPinnedRef.current = true;
    isPrependingRef.current = false;
    setShowScrollBtn(false);
    addLocalMessage('user', msg, currentOnboardingStep);
    streamControllerRef.current = startChatStream(dispatch, msg, currentOnboardingStep, aiHelperFieldLabel, undefined, handleOpenTicketForm);
    if (aiHelperFieldLabel) {
      dispatch(clearAiHelperContext());
    }
    setInputValue('');
    requestAnimationFrame(smoothScrollToBottom);
  };

  const handleOpenTicketForm = () => {
    setTicketFormOpen(true);
  };

  const handleTicketConfirm = () => {
    if (!ticketConfirm) return;
    dispatch(confirmCreateTicket({
      title: ticketConfirm.title,
      description: ticketConfirm.description || ticketConfirm.summary || '',
      comment: ticketComment.trim() || undefined,
      issueType: ticketConfirm.issueType,
      processStep: ticketConfirm.processStep,
      category: ticketConfirm.category,
      timestamp: new Date(),
    }));
  };


  const handleTicketCancel = () => {
    dispatch(cancelTicketConfirm());
  };  

  const [ticketComment, setTicketComment] = useState('');
  const [ticketFormOpen, setTicketFormOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetailItem | null>(null);

  const handleTicketBadgeClick = () => {
    dispatch(toggleTicketList());
  };

  useEffect(() => {
    if (!ticketConfirm) {
      setTicketFormOpen(false);
      setTicketComment('');
    }
  }, [ticketConfirm]);

  if (!isOpen) return null;

  if (selectedTicket) {
    return (
      <div className='chat-panel'>
        <TicketDetail
          ticket={selectedTicket}
          comments={[]}
          onBack={() => setSelectedTicket(null)}
          onAddComment={(comment: string) => {
            console.log('New comment:', comment);
          }}
        />
      </div>
    );
  }

  if (showTicketList) {
    return (
      <div className='chat-panel'>
        <TicketList
          tickets={tickets}
          onBack={() => dispatch(toggleTicketList())}
          onTicketClick={(ticket) => setSelectedTicket(ticket)}
        />
      </div>
    );
  }

  if (ticketFormOpen) {
    return (
      <div className='chat-panel'>
        <TicketForm
          ticketConfirm={ticketConfirm}
          ticketComment={ticketComment}
          setTicketComment={setTicketComment}
          ticketFormOpen={ticketFormOpen}
          setTicketFormOpen={setTicketFormOpen}
          handleTicketConfirm={handleTicketConfirm}
          ticketCreating={ticketCreating}
          ticketError={ticketError}
          currentOnboardingStep={currentOnboardingStep}
          handleTicketCancel={handleTicketCancel}
        />
      </div>
    );
  }

  return (
    <div className='chat-panel'>
      <div className='chat-panel-header'>
        <div className='chat-panel-header-avatar'>
          <img
            src={imgURL['guide-logo']}
            alt="Guide"
            className="chat-panel-header-avatar-img"
          />
        </div>
        <div className='chat-panel-header-text'>
          <div className='chat-panel-header-title'>Guide</div>
          <span className='chat-panel-header-subtitle'>Chat</span>
        </div>
        <div className='chat-panel-actions'>
          {ticketCount > 0 && (
            <div className='chat-ticket-badge' onClick={handleTicketBadgeClick} title="View tickets">
              <img src={imgURL['chat-ticket']} alt="Tickets" />
              <span className='chat-ticket-count'>{ticketCount}</span>
            </div>
          )}
          <button className='chat-header-btn' title='Close' onClick={onClose}>
            <img src={imgURL['chat-minimize']} alt="Close" />
          </button>
        </div>
      </div>

      <div
        className='chat-panel-body'
        ref={scrollBodyRef}
        onScroll={handleScroll}
      >
        {isLoadingMoreHistory && (
          <div className='chat-history-loading'>Loading earlier messages…</div>
        )}
        {!isLoadingMoreHistory && hasMoreHistory && (
          <div className='chat-history-loading'>
            Showing {messages.length} of {totalHistoryCount} messages — scroll up to load more
          </div>
        )}

        {
          messages.map((msg: any, idx: number) => {
            const isLast = idx === messages.length - 1;
            const streaming =
              isLast && isLoading && msg.role === 'assistant';

            const prevMsg = messages[idx - 1];
            const currentStep = msg.current_step;
            const prevStep = prevMsg?.current_step;
            const showStepDivider = currentStep && currentStep !== prevStep;

            if (streaming && !msg.content) {
              return <ChatMessage key={`${idx}-0`} message={{ ...msg, content: msg.content }} streaming={streaming && 1 === 1} />;
            }
            if (msg?.type === 'error') {
              return (
                <div key={idx} className='chat-msg-error'>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>{msg.content}</span>
                </div>
              );
            }
            if (msg?.type == 'ticket_created') {
              let ticket_data = {};
              try {
                ticket_data = JSON.parse(msg.content);
              }
              catch (e) {
                console.error("Failed to parse ticket data", e);
              }
              if (!ticket_data || Object.keys(ticket_data).length === 0) {
                return null;
              }
              return <TicketCreationSuccess hideStatus={!msg.fromLocal} ticket_data={ticket_data} />;
            }

            const messageParts = msg.role === 'assistant' ? msg.content.split('\n')?.filter((part: string) => part.trim() !== '') : [msg.content] ;
            return (
              <React.Fragment key={idx}>
                {showStepDivider && (
                  <div className="chat-msg-step-divider">
                    <span className="chat-msg-step-divider-text">
                      {BACKEND_STEP_TO_DISPLAY_LABEL[msg.current_step] ||
                        msg.current_step.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                    </span>
                  </div>
                )}
                {messageParts.map((part: string, partIdx: number) => (
                  <ChatMessage
                    key={`${idx}-${partIdx}`} message={{ ...msg, content: part }} streaming={streaming && partIdx === 1} />
                ))}
              </React.Fragment>
            );
          })}
        <div ref={messagesEndRef} />
        {(
          messages?.length === 0 ||
          messages?.[messages.length - 1]?.current_step != currentOnboardingStep
        ) &&
          (
            <div className='suggestion-container'>
              {suggestions?.map((q: string, qi: number) => (
                <ChatMessage
                  key={`suggestion-${qi}`}
                  handleSuggestionClick={() => handleSend(q)}
                  message={{
                    role: 'suggestion',
                    content: q,
                  }} streaming={false} />
              ))}
            </div>
          )}
        {ticketConfirm?.visible && (
          <ChatMessage
            key={`suggestion-banner`}
            handleSuggestionClick={handleOpenTicketForm}
            message={{
              role: 'suggestion',
              content: 'Raise a support ticket',
            }} streaming={false} />
        )}

        {showScrollBtn && (
          <button
            className='chat-scroll-fab'
            onClick={smoothScrollToBottom}
            title='Scroll to bottom'
            aria-label='Scroll to bottom'
          >
            New ↓
          </button>
        )}
      </div>
      <div className='chat-panel-footer'>
        <div className='chat-input-row'>
          <textarea
            id='chat-input'
            className='chat-input'
            placeholder='Type your query'
            value={inputValue}
            onChange={(e: any) => setInputValue(e.target.value)}
            disabled={isLoading}
            maxLength={280}/>
          <button
            className='chat-send-btn'
            onClick={() => handleSend()}
            disabled={!inputValue.trim() || isLoading}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </div>
        <div className='chat-privacy-policy'>
          By chatting with us, you agree to our <a className='privacy-policy-link' href="https://www.pinelabs.com/terms-of-use/ai-chatbot" target="_blank" rel="noopener noreferrer">Terms of use</a> and <a className='privacy-policy-link' href="https://www.pinelabs.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ chatbotReducer, loginReducer, onboardingReducer }: any) => ({
  messages: chatbotReducer.messages,
  isLoading: chatbotReducer.isLoading,
  suggestions: chatbotReducer.suggestions || [],
  ticketConfirm: chatbotReducer.ticketConfirm || null,
  ticketCreating: chatbotReducer.ticketCreating || false,
  ticketError: chatbotReducer.ticketError || null,
  ticketCount: chatbotReducer.ticketCount || 0,
  showTicketList: chatbotReducer.showTicketList || false,
  tickets: chatbotReducer.tickets || [],
  currentOnboardingStep: onboardingReducer?.stepperDetails?.data?.current_step || '',
  aiHelperFieldLabel: chatbotReducer.aiHelperFieldLabel || null,
  verificationErrorContext: chatbotReducer.verificationErrorContext || null,
  hasMoreHistory: chatbotReducer.hasMoreHistory || false,
  isLoadingMoreHistory: chatbotReducer.isLoadingMoreHistory || false,
  totalHistoryCount: chatbotReducer.totalHistoryCount || 0,
});

const mapDispatchToProps = (dispatch: any) => {
  return {
    addLocalMessage: (role: 'user' | 'assistant', content: string, currentStep?: string) =>
      dispatch(addLocalChatMessage(role, content, currentStep)),
    loadMoreHistory: (before: string) => dispatch(loadMoreChatHistoryAction(before)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatPanel);
